import { Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import {
    ContentProvider, InstanceProvider, JourneyProvider,
    TemplateProvider, GroupProvider,
    ExternalContentProvider, OnboardingProvider
} from '@lighty';
import { HttpHelper } from '@helpers';

@Injectable()
export class OnboardingResolver implements Resolve<any> {
    constructor(private onboardingProvider: OnboardingProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.paramMap.get('slug')) {
            const params = {
                company_id: route.parent.paramMap.get('slug')
            };
            return this.onboardingProvider.getOnboardingContent(params);
        }
    }
}

@Injectable()
export class catalogeWatchResolver implements Resolve<any> {
    constructor(private contentProvider: ContentProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.parent.paramMap.get('slug')) {
            const params = {
                company_id: route.parent.parent.paramMap.get('slug'),
                per_page: 50
            };
            
            return forkJoin({
                catalogue: this.contentProvider.getCatalogue(params).pipe(
                    catchError(() => of({ catalogue: [], pagination: null }))
                ),
                contents: this.contentProvider.getContents(params).pipe(
                    catchError(() => of({ contents: [] }))
                )
            }).pipe(
                map((data: any) => {
                    // Enriquecer el catálogo con información de suscripción de contents
                    const subscribedIds = new Set();
                    (data.contents?.contents || []).forEach((content: any) => {
                        if (content.data?.id) {
                            subscribedIds.add(content.data.id);
                        }
                    });
                    
                    // Enriquecer los items del catálogo con el flag subscribed
                    const enrichedCatalogue = (data.catalogue?.catalogue || []).map((item: any) => {
                        const itemId = item.data?.id || item.id;
                        return {
                            ...item,
                            data: {
                                ...item.data,
                                subscribed: subscribedIds.has(itemId) || item.data?.subscribed || false
                            }
                        };
                    });
                    
                    return {
                        catalogue: enrichedCatalogue,
                        pagination: data.catalogue?.pagination
                    };
                }),
                catchError(() => of({ catalogue: [], pagination: null }))
            );
        }
    }
}


@Injectable()
export class ContentResolver implements Resolve<any> {
    constructor(private contentProvider: ContentProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.parent.paramMap.get('slug')) {
            const params = {
                company_id: route.parent.parent.paramMap.get('slug')
            };
            return this.contentProvider.getContents(params);
        }
    }
}

@Injectable()
export class ContentSavedResolver implements Resolve<any> {
    constructor(private contentProvider: ContentProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.parent.paramMap.get('slug')) {
            const params = {
                company_id: route.parent.parent.paramMap.get('slug')
            };
            return this.contentProvider.getSavedContents(params);
        }
    }
}

@Injectable()
export class CatalogueResolver implements Resolve<any> {
    constructor(private contentProvider: ContentProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.parent.paramMap.get('slug')) {
            const params = HttpHelper.cleanParams({
                company_id: route.parent.parent.paramMap.get('slug'),
                // langs: route.parent.parent.data.me.learningLanguages.map(lang => lang.id).join(',')
            });
            return this.contentProvider.getOverview(params);
        }
    }
}

@Injectable()
export class CatalogueSkillsResolver implements Resolve<any> {
    constructor(private contentProvider: ContentProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.parent.paramMap.get('slug')) {
            const params = {
                company_id: route.parent.parent.paramMap.get('slug'),
                browse: route.paramMap.get('type'),
            };

            if (route.paramMap.get('thematicId')) {
                if (route.paramMap.get('level')) {
                    return this.contentProvider.getCatalogueSkillLevel(parseInt(route.paramMap.get('thematicId'), 10), parseInt(route.paramMap.get('level'), 10), params);
                }
                return this.contentProvider.getCatalogueSkill(parseInt(route.paramMap.get('thematicId'), 10), params);
            }
            return this.contentProvider.getCatalogueSkills(params);
        }
    }
}

@Injectable()
export class CommunityResolver implements Resolve<any> {
    constructor(private groupProvider: GroupProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.parent.paramMap.get('slug')) {
            if (route.paramMap.get('communityId')) {
                const paramsGroup = {
                    include: 'account,users_preview',
                };
                return this.groupProvider.getGroup(
                    route.parent.parent.paramMap.get('slug'),
                    parseInt(route.paramMap.get('communityId'), 10),
                    paramsGroup
                );
            }

            const params = {
                include: 'account,subscription,users_preview',
                status: 'allGroups'
            };
            return this.groupProvider.get(route.parent.parent.paramMap.get('slug'), params);
        }
    }
}

@Injectable()
export class LearningGroupsResolver implements Resolve<any> {
    constructor(private groupProvider: GroupProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        return this.groupProvider.getLearningGroups(route.parent.parent.paramMap.get('slug'));
    }
}

@Injectable()
export class LearningGroupResolver implements Resolve<any> {
    constructor(private groupProvider: GroupProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> | Promise<any> | any {
        return this.groupProvider.getLearningGroup(route.parent.parent.paramMap.get('slug'), route.params.groupId);
    }
}

@Injectable()
export class InstanceResolver implements Resolve<any> {
    constructor(private instanceProvider: InstanceProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        return this.instanceProvider.get(parseInt(route.paramMap.get('instanceId'), 10));
    }
}

@Injectable()
export class InstanceCorrectionsResolver implements Resolve<any> {
    constructor(private instanceProvider: InstanceProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        return this.instanceProvider.getCorrections(parseInt(route.paramMap.get('instanceId'), 10));
    }
}

@Injectable()
export class InstanceSummaryResolver implements Resolve<any> {
    constructor(private instanceProvider: InstanceProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        return this.instanceProvider.getSummary(parseInt(route.paramMap.get('instanceId'), 10));
    }
}

@Injectable()
export class TemplateResolver implements Resolve<any> {
    constructor(private templateProvider: TemplateProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.parent.paramMap.get('slug')) {
            const params = HttpHelper.cleanParams({
                include: 'course_camps,course_camps.courseCampSessions,course_camps.course.events,course_camps.course.events.event_sessions,course_camps.seats.users',
                company_id: route.parent.parent.paramMap.get('slug')
            });
            return this.templateProvider.get(parseInt(route.paramMap.get('templateId'), 10), params);
        }
    }
}

@Injectable()
export class TemplateSummaryResolver implements Resolve<any> {
    constructor(private templateProvider: TemplateProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        return this.templateProvider.getSummary(parseInt(route.paramMap.get('templateId'), 10));
    }
}

/**
 * No bloquea la navegación: en vez de esperar la respuesta real, envuelve el observable
 * ('journey$') y lo devuelve YA (Angular resuelve `of(...)` en un microtask). El componente lee
 * `journey$` de `route.parent.data` y se suscribe él mismo -- eso permite pintar un skeleton de
 * inmediato en vez de bloquear el RouterOutlet hasta que `traject/{id}` (que puede tardar
 * segundos en trajects grandes) responda. Ver `JourneyProvider.getDetail()`.
 */
@Injectable()
export class JourneyResolver implements Resolve<any> {
    constructor(private journeyProvider: JourneyProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.parent.paramMap.get('slug')) {
            const journey$ = this.journeyProvider.getDetail(
                route.parent.parent.paramMap.get('slug'),
                parseInt(route.paramMap.get('journeyId'), 10)
            ).pipe(shareReplay(1));

            return of({ journey$ });
        }

        return of({ journey$: of(null) });
    }
}

/**
 * Mismo motivo que `JourneyResolver`: no bloquear la navegación por las correcciones, que
 * tampoco son necesarias para el primer render.
 */
@Injectable()
export class JourneyDetailsCorrectionsResolver implements Resolve<any> {
    constructor(private journeyProvider: JourneyProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.parent.paramMap.get('slug')) {
            const corrections$ = this.journeyProvider.getCorrections(
                route.parent.parent.paramMap.get('slug'),
                parseInt(route.paramMap.get('journeyId'), 10)
            ).pipe(
                catchError(() => of([])),
                shareReplay(1)
            );

            return of({ corrections$ });
        }

        return of({ corrections$: of([]) });
    }
}

@Injectable()
export class EdflexResolver implements Resolve<any> {
    constructor(private externalContentProvider: ExternalContentProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.parent.paramMap.get('slug')) {
            const params = HttpHelper.cleanParams({
                company_id: route.parent.parent.paramMap.get('slug'),
            });
            return this.externalContentProvider.getEdflex(parseInt(route.paramMap.get('edflexId'), 10), params);
        }
    }
}

@Injectable()
export class ExternalContentResolver implements Resolve<any> {
    constructor(private externalContentProvider: ExternalContentProvider) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> {
        if (route.parent.parent.paramMap.get('slug')) {
            let context = '';
            switch (route.paramMap.get('externalContentContext')) {
                case 'video':
                case 'external_video':
                    context = 'video';
                    break;
                default:
                    context = 'content';
                    break;
            }

            const params = HttpHelper.cleanParams({
                context,
                company_id: route.queryParams.companyId ? route.queryParams.companyId : route.parent.parent.paramMap.get('slug'),
            });

            return this.externalContentProvider.getExternalContentDetails(parseInt(route.paramMap.get('externalContentId'), 10), params);
        }
    }
}

@Injectable()
export class ExternalContentApiCallCopyResolver implements Resolve<any> {
    constructor(private externalContentProvider: ExternalContentProvider) { }

    resolve(route: ActivatedRouteSnapshot) {
        if (route.parent.parent.paramMap.get('slug')) {
            let context = '';
            switch (route.paramMap.get('externalContentContext')) {
                case 'video':
                case 'external_video':
                    context = 'video';
                    break;
                default:
                    context = 'content';
                    break;
            }
            const params = HttpHelper.cleanParams({
                context,
                company_id: route.queryParams.companyId ? route.queryParams.companyId : route.parent.parent.paramMap.get('slug'),
            });
            return () => this.externalContentProvider.getExternalContentDetails(parseInt(route.paramMap.get('externalContentId'), 10), params);
        }
    }
}
